import { get, create, updatePatch } from "./apiService";
import { buildTransactionListQueryParams } from "./transactionListParams";
import {
  resolveBusinessRoute,
  resolveBusinessUrlWithQuery,
  withBusinessContext,
} from "../utils/businessRoute";
import { encodeApiPathSegment } from "../utils/apiPath";
import { assertValidAmount } from "../utils/inputValidation";
import type {
  ApiResponse,
  TransactionStatus,
  V2Transaction,
  CryptoSendRequest,
  CryptoSendQuoteRequest,
  FiatWithdrawRequest,
  FiatWithdrawQuoteRequest,
  FiatDepositRequest,
  CryptoReceiveRequest,
  PaymentRequest,
  TransferRequest,
  DonationRequest,
  Zar8MintRequest,
  Zar8BurnRequest,
  TransactionListParams,
} from "../types/api";

interface TransactionPaginationMeta {
  page?: number;
  limit?: number;
  total?: number;
  totalPages?: number;
}

interface TransactionPagePayload<T> {
  transactions?: T[];
  total?: number;
  page?: number;
  limit?: number;
  pagination?: TransactionPaginationMeta;
}

function dedupeTradeLedgerLegs<T extends Record<string, unknown>>(
  transactions: T[]
): T[] {
  const byTradeId = new Map<string, { tx: T; index: number }>();
  const result: T[] = [];

  for (const tx of transactions) {
    const type = typeof tx.type === "string" ? tx.type : "";
    const trade = tx.trade as { _id?: string } | null | undefined;
    const tradeId =
      trade &&
      typeof trade === "object" &&
      trade !== null &&
      typeof trade._id === "string" &&
      trade._id.length > 0
        ? trade._id
        : "";

    if (type === "trade" && tradeId) {
      const activity = typeof tx.activity === "string" ? tx.activity : "";
      const existing = byTradeId.get(tradeId);

      if (!existing) {
        const index = result.length;
        result.push(tx);
        byTradeId.set(tradeId, { tx, index });
        continue;
      }

      const existingActivity =
        typeof existing.tx.activity === "string" ? existing.tx.activity : "";
      if (activity === "incoming" && existingActivity !== "incoming") {
        result[existing.index] = tx;
        byTradeId.set(tradeId, { tx, index: existing.index });
      }
      continue;
    }

    result.push(tx);
  }

  return result;
}

interface TransactionAssetRef {
  _id?: string;
  symbol?: string;
  name?: string;
  image?: string;
  type?: string;
}

interface RawTradeRef {
  side?: string;
  price?: string | number | { $numberDecimal?: string };
  incomingAmount?: string | number | { $numberDecimal?: string };
  outgoingAmount?: string | number | { $numberDecimal?: string };
  total?: string | number | { $numberDecimal?: string };
  totalAmount?: string | number | { $numberDecimal?: string };
  amount?: string | number | { $numberDecimal?: string };
  incomingAsset?: string | TransactionAssetRef;
  outgoingAsset?: string | TransactionAssetRef;
  [key: string]: unknown;
}

interface RawActiveTransaction extends RawV2Transaction {
  cryptoAsset?: string;
  assetIcon?: string;
  transactionType?: string;
  chain?: string | { chain?: string };
  network?: string;
  chainName?: string;
  transactionHash?: string;
  explorerUrl?: string;
  explorerTxPath?: string;
  statusUnavailable?: boolean;
  failureReason?: string;
  crypto?: {
    txHash?: string;
    walletAddress?: string;
    blockchainId?: string | { symbol?: string; name?: string };
    network?: string;
    chainName?: string;
    explorerUrl?: string;
    explorerTxPath?: string;
    failureReason?: string;
    statusUnavailable?: boolean;
  } | null;
}

interface RawV2Transaction extends Record<string, unknown> {
  _id: string;
  type?: string;
  action?: string;
  activity?: string;
  status?: string;
  asset?: string | TransactionAssetRef;
  assetSymbol?: string;
  currency?: string | TransactionAssetRef;
  amount?: string | number | { $numberDecimal?: string };
  totalAmount?: string | number | { $numberDecimal?: string };
  total?: string | number | { $numberDecimal?: string };
  fee?: string | number | { $numberDecimal?: string };
  reference?: string;
  notes?: string;
  createdAt?: string;
  updatedAt?: string;
  trade?: RawTradeRef | null;
}

interface LegacyTransactionPage<T> {
  results: T[];
  totalPages: number;
  totalResults: number;
  page: number;
  limit: number;
}

interface LegacyTransactionServiceResponse<T> {
  data: {
    data: LegacyTransactionPage<T>;
  };
}

interface ActiveTransactionResponse<T> {
  data: {
    status: boolean;
    data: T;
  };
}

function wrapData<T>(data: T): { data: ApiResponse<T> } {
  return {
    data: {
      status: true,
      data,
    },
  };
}

function splitBusinessId<T extends Record<string, unknown> | undefined>(
  body: T
): {
  businessId: string | null;
  rest: T;
} {
  if (!body || typeof body !== "object") {
    return { businessId: null, rest: body };
  }
  const { businessId: rawBusinessId, ...rest } = body as Record<
    string,
    unknown
  >;
  const businessId =
    typeof rawBusinessId === "string" && rawBusinessId.trim()
      ? rawBusinessId.trim()
      : null;
  return { businessId, rest: rest as T };
}

function getAssetSymbol(value: unknown): string {
  if (typeof value === "string") {
    return value;
  }

  if (
    value !== null &&
    typeof value === "object" &&
    "symbol" in value &&
    typeof (value as TransactionAssetRef).symbol === "string"
  ) {
    return (value as TransactionAssetRef).symbol || "";
  }

  return "";
}

function mapLegacyTransactionType(type?: string): string {
  switch (type) {
    case "trade":
      return "UserTrade";
    case "fiat":
    case "fiat_deposit":
    case "fiat_withdraw":
      return "FiatTransaction";
    case "crypto":
    case "crypto_send":
    case "crypto_receive":
      return "CryptoTransaction";
    case "payment":
      return "Payment";
    case "transfer":
      return "Transfer";
    case "donation":
      return "Donation";
    case "zar8_mint":
    case "zar8_burn":
    case "zar8":
      return "Zar8Transaction";
    default:
      return "Transaction";
  }
}

function mapLegacyTransactionSubtype(
  type?: string,
  action?: string,
  activity?: string
): string {
  if (type === "trade") {
    return action || "";
  }

  switch (type) {
    case "fiat_deposit":
      return "deposit";
    case "fiat_withdraw":
      return "withdrawal";
    case "crypto_send":
      return "send";
    case "crypto_receive":
      return "receive";
    case "payment":
      return activity || "payment";
    case "transfer":
      return activity || "transfer";
    case "donation":
      return "donation";
    case "zar8_mint":
      return "mint";
    case "zar8_burn":
      return "burn";
    default:
      return action || activity || "";
  }
}

function extractDecimalString(
  value: string | number | { $numberDecimal?: string } | undefined
): string {
  if (typeof value === "number") {
    return String(value);
  }

  if (typeof value === "string") {
    return value;
  }

  if (
    value &&
    typeof value === "object" &&
    typeof value.$numberDecimal === "string"
  ) {
    return value.$numberDecimal;
  }

  return "0";
}

function normalizeTradePrice(
  trade: RawTradeRef
): string | number | { $numberDecimal?: string } | undefined {
  const rawPrice = trade.price;
  if (rawPrice === undefined || rawPrice === null) {
    return rawPrice;
  }

  const sideValue =
    typeof trade.type === "string"
      ? trade.type
      : typeof (trade as { side?: unknown }).side === "string"
        ? ((trade as { side?: string }).side as string)
        : "";
  const isSell = sideValue.toLowerCase() === "sell";
  if (!isSell) {
    return rawPrice;
  }

  const priceNum = parseFloat(extractDecimalString(rawPrice));
  if (!Number.isFinite(priceNum) || priceNum <= 0 || priceNum >= 1) {
    return rawPrice;
  }

  return String(1 / priceNum);
}

function normalizeStatusLabel(status?: string): string {
  if (!status) {
    return "Pending";
  }

  return status
    .split(/[_\s-]+/)
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1).toLowerCase())
    .join(" ");
}

function normalizeTransactionStatusKey(status?: string): TransactionStatus {
  const normalized = (status || "").trim().toLowerCase();

  if (
    normalized === "completed" ||
    normalized === "complete" ||
    normalized === "confirmed"
  ) {
    return "completed";
  }

  if (normalized === "failed" || normalized === "rejected") {
    return "failed";
  }

  if (
    normalized === "cancelled" ||
    normalized === "canceled" ||
    normalized === "expired"
  ) {
    return "cancelled";
  }

  if (
    normalized === "processing" ||
    normalized === "confirming" ||
    normalized === "broadcasting" ||
    normalized === "queued" ||
    normalized === "pending screening"
  ) {
    return "processing";
  }

  return "pending";
}

function getAssetRef(value: unknown): TransactionAssetRef | null {
  if (!value || typeof value !== "object") {
    return null;
  }

  return value as TransactionAssetRef;
}

function deriveTransactionAsset(
  transaction: RawActiveTransaction,
  trade?: RawTradeRef | null
): string {
  const incomingSymbol = getAssetSymbol(trade?.incomingAsset);
  const outgoingSymbol = getAssetSymbol(trade?.outgoingAsset);
  const transactionAsset =
    getAssetSymbol(transaction.asset) ||
    transaction.assetSymbol ||
    getAssetSymbol(transaction.currency) ||
    transaction.cryptoAsset ||
    "";
  const pair = typeof trade?.pair === "string" ? trade.pair : "";
  const [baseAsset, quoteAsset] = pair
    .split("-")
    .map((part) => part.toUpperCase())
    .filter(Boolean);

  if (trade?.side === "sell") {
    return (
      outgoingSymbol ||
      baseAsset ||
      transactionAsset ||
      quoteAsset ||
      incomingSymbol ||
      ""
    );
  }

  return (
    incomingSymbol ||
    baseAsset ||
    transactionAsset ||
    quoteAsset ||
    outgoingSymbol ||
    ""
  );
}

function deriveAssetIcon(
  transaction: RawActiveTransaction,
  trade?: RawTradeRef | null
): string | undefined {
  const transactionAsset = getAssetRef(transaction.asset);
  const incomingAsset = getAssetRef(trade?.incomingAsset);
  const outgoingAsset = getAssetRef(trade?.outgoingAsset);

  if (trade?.side === "sell") {
    return (
      outgoingAsset?.image ||
      transactionAsset?.image ||
      incomingAsset?.image ||
      getAssetRef(transaction.currency)?.image
    );
  }

  return (
    incomingAsset?.image ||
    transactionAsset?.image ||
    outgoingAsset?.image ||
    getAssetRef(transaction.currency)?.image
  );
}

export function normalizeActiveTransaction(
  transaction: RawActiveTransaction
): V2Transaction {
  const trade = transaction.trade;
  const normalizedTransaction = normalizeTransaction(transaction);
  const cryptoDetails = transaction.crypto;
  const transactionAsset = deriveTransactionAsset(transaction, trade);
  const blockchainValue = cryptoDetails?.blockchainId;
  const chainValue =
    typeof blockchainValue === "string"
      ? blockchainValue
      : blockchainValue?.symbol || blockchainValue?.name;

  return {
    ...normalizedTransaction,
    cryptoAsset: transaction.cryptoAsset || transactionAsset,
    assetIcon: transaction.assetIcon || deriveAssetIcon(transaction, trade),
    transactionType:
      transaction.type === "trade"
        ? transaction.action || normalizedTransaction.transactionSubtype
        : transaction.transactionType ||
          normalizedTransaction.transactionSubtype,
    status: normalizeTransactionStatusKey(transaction.status),
    statusLabel: normalizeStatusLabel(transaction.status),
    amount: extractDecimalString(transaction.amount),
    chain:
      transaction.chain || chainValue || cryptoDetails?.network || undefined,
    network: transaction.network || cryptoDetails?.network,
    chainName:
      transaction.chainName ||
      (typeof blockchainValue === "object" ? blockchainValue?.name : undefined),
    transactionHash: transaction.transactionHash || cryptoDetails?.txHash,
    explorerUrl: transaction.explorerUrl || cryptoDetails?.explorerUrl,
    explorerTxPath: transaction.explorerTxPath || cryptoDetails?.explorerTxPath,
    statusUnavailable:
      typeof transaction.statusUnavailable === "boolean"
        ? transaction.statusUnavailable
        : cryptoDetails?.statusUnavailable,
    failureReason: transaction.failureReason || cryptoDetails?.failureReason,
  };
}

function normalizeTransaction(transaction: RawV2Transaction): V2Transaction {
  const assetSymbol =
    getAssetSymbol(transaction.asset) || transaction.assetSymbol || "";
  const currencySymbol = getAssetSymbol(transaction.currency);
  const trade = transaction.trade;

  const tradeIncomingAsset = getAssetSymbol(trade?.incomingAsset);
  const tradeOutgoingAsset = getAssetSymbol(trade?.outgoingAsset);
  const normalizedSubtype = mapLegacyTransactionSubtype(
    transaction.type,
    transaction.action,
    transaction.activity
  );

  const fallbackIncomingAsset =
    normalizedSubtype === "buy" ? assetSymbol : assetSymbol || currencySymbol;
  const fallbackOutgoingAsset =
    normalizedSubtype === "buy"
      ? currencySymbol || "ZAR"
      : currencySymbol ||
        assetSymbol ||
        tradeOutgoingAsset ||
        tradeIncomingAsset ||
        "";

  return {
    ...transaction,
    asset: assetSymbol,
    currency:
      currencySymbol || (normalizedSubtype === "buy" ? "ZAR" : assetSymbol),
    status: normalizeTransactionStatusKey(transaction.status),
    transactionType: mapLegacyTransactionType(transaction.type),
    transactionSubtype: normalizedSubtype,
    transactionRef: trade
      ? {
          ...trade,
          incomingAsset: tradeIncomingAsset || fallbackIncomingAsset,
          outgoingAsset: tradeOutgoingAsset || fallbackOutgoingAsset,
          incomingAmount: trade.incomingAmount,
          outgoingAmount: trade.outgoingAmount,
          total: trade.total,
          totalAmount: trade.totalAmount,
          amount: trade.amount,
          price: normalizeTradePrice(trade),
        }
      : undefined,
  } as V2Transaction;
}

const normalizeTransactionPage = <T>(
  payload: TransactionPagePayload<T>
): { data: { data: LegacyTransactionPage<T> } } => {
  const pag = payload.pagination;
  const page = Math.max(1, payload.page ?? pag?.page ?? 1);
  const limit = Math.max(1, payload.limit ?? pag?.limit ?? 20);
  const totalResults = Math.max(0, payload.total ?? pag?.total ?? 0);
  const apiTotalPages = pag?.totalPages;
  const totalPages =
    typeof apiTotalPages === "number" && apiTotalPages >= 1
      ? apiTotalPages
      : Math.max(1, limit > 0 ? Math.ceil(totalResults / limit) : 1);

  const rawList = (payload.transactions || []) as unknown as Record<
    string,
    unknown
  >[];
  const deduped = dedupeTradeLedgerLegs(rawList) as unknown as T[];

  return {
    data: {
      data: {
        results: deduped.map((transaction) =>
          normalizeTransaction(transaction as RawV2Transaction)
        ) as T[],
        totalPages,
        totalResults,
        page,
        limit,
      },
    },
  };
};

const inFlightTransactionRequests = new Map<
  string,
  Promise<LegacyTransactionServiceResponse<V2Transaction>>
>();

const buildTransactionRequestKey = (
  queryParams: Record<string, string>
): string => {
  const entries = Object.entries(queryParams).sort(([left], [right]) =>
    left.localeCompare(right)
  );
  return entries.map(([key, value]) => `${key}=${value}`).join("&");
};

export const getTransactions = async (
  params?: TransactionListParams
): Promise<LegacyTransactionServiceResponse<V2Transaction>> => {
  const queryParams = buildTransactionListQueryParams(params);
  const businessId =
    typeof queryParams.businessId === "string" && queryParams.businessId.trim()
      ? queryParams.businessId.trim()
      : null;
  delete queryParams.businessId;

  const requestKey = buildTransactionRequestKey({
    ...queryParams,
    ...(businessId ? { __scope: `business:${businessId}` } : {}),
  });

  const existing = inFlightTransactionRequests.get(requestKey);
  if (existing) {
    return existing;
  }

  const pending = (async () => {
    const response = await get<TransactionPagePayload<V2Transaction>>(
      resolveBusinessRoute(businessId, "/api/v2/transactions"),
      withBusinessContext(businessId, queryParams)
    );
    return normalizeTransactionPage(response);
  })().finally(() => {
    if (inFlightTransactionRequests.get(requestKey) === pending) {
      inFlightTransactionRequests.delete(requestKey);
    }
  });

  inFlightTransactionRequests.set(requestKey, pending);
  return pending;
};

export const getTransactionById = async (
  transactionId: string,
  businessId?: string | null
): Promise<{ data: ApiResponse<V2Transaction> }> => {
  const response = await get<V2Transaction>(
    resolveBusinessRoute(
      businessId,
      `/api/v2/transactions/${encodeApiPathSegment(transactionId, "transactionId")}`
    ),
    withBusinessContext(businessId)
  );
  return wrapData(response);
};

export const getUserTransactionHistory = async (
  _userId?: string,
  page?: number,
  accountId?: string,
  limit?: number,
  listParams: Omit<TransactionListParams, "page" | "limit" | "accountId"> = {}
): Promise<LegacyTransactionServiceResponse<V2Transaction>> => {
  return getTransactions({
    page,
    limit,
    accountId,
    ...listParams,
  });
};

export const getTransactionHistory = async (
  accountId?: string
): Promise<{
  data: {
    data: LegacyTransactionPage<V2Transaction>;
  };
}> => {
  return getTransactions(accountId ? { accountId } : undefined);
};

export const getMyFiatTransactions = async (
  page: number = 1,
  accountId?: string
): Promise<LegacyTransactionServiceResponse<V2Transaction>> => {
  return getTransactions({
    page,
    limit: 100,
    type: "fiat_withdraw",
    accountId,
  });
};

export const getUserPayTransactions = async (
  accountId?: string,
  businessId?: string
): Promise<{
  data: {
    data: LegacyTransactionPage<V2Transaction>;
  };
}> => {
  return getTransactions({
    type: "payment",
    limit: 1,
    accountId: businessId ? undefined : accountId,
    businessId,
  });
};

export const sendCrypto = async (
  data: CryptoSendRequest & { businessId?: string | null }
): Promise<{ data: ApiResponse<V2Transaction> }> => {
  assertValidAmount(data.amount);
  const { businessId, rest } = splitBusinessId(
    data as unknown as Record<string, unknown>
  );
  const response = await create<V2Transaction>(
    resolveBusinessUrlWithQuery(businessId, "/api/v2/transactions/crypto/send"),
    rest
  );
  return wrapData(response);
};

export interface CryptoReceiveAddressResult {
  address: string;
  tag?: string | null;
  isTagRequired?: boolean;
  asset?: string;
}

export const getCryptoReceiveAddress = async (
  data: CryptoReceiveRequest & { businessId?: string | null }
): Promise<{
  data: ApiResponse<CryptoReceiveAddressResult>;
}> => {
  const { businessId, rest } = splitBusinessId(
    data as unknown as Record<string, unknown>
  );
  const response = await create<CryptoReceiveAddressResult>(
    resolveBusinessUrlWithQuery(
      businessId,
      "/api/v2/transactions/crypto/receive"
    ),
    rest
  );
  return wrapData(response);
};

export const getActiveTransactions = async (
  accountId?: string,
  businessId?: string | null
): Promise<{
  data: ApiResponse<V2Transaction[]>;
}> => {
  const url = resolveBusinessRoute(
    businessId,
    "/api/v2/transactions/crypto/active"
  );
  const queryParams = withBusinessContext(
    businessId,
    !businessId && accountId ? { accountId } : {}
  );
  const response =
    Object.keys(queryParams).length > 0
      ? await get<RawActiveTransaction[]>(url, queryParams)
      : await get<RawActiveTransaction[]>(url);

  return {
    data: {
      status: true,
      data: response.map(normalizeActiveTransaction),
    },
  } as ActiveTransactionResponse<V2Transaction[]>;
};

export const dismissTransaction = async (
  transactionId: string,
  businessId?: string | null
): Promise<{ data: ApiResponse }> => {
  const response = await updatePatch(
    resolveBusinessRoute(
      businessId,
      `/api/v2/transactions/crypto/${encodeApiPathSegment(transactionId, "transactionId")}/dismiss`
    ),
    withBusinessContext(businessId),
    {}
  );
  return wrapData(response);
};

export const withdraw = async (
  data: FiatWithdrawRequest & { businessId?: string | null }
): Promise<{ data: ApiResponse<V2Transaction> }> => {
  assertValidAmount(data.amount);
  const { businessId, rest } = splitBusinessId(
    data as unknown as Record<string, unknown>
  );
  const response = await create<V2Transaction>(
    resolveBusinessUrlWithQuery(
      businessId,
      "/api/v2/transactions/fiat/withdraw"
    ),
    rest
  );
  return wrapData(response);
};

export const deposit = async (
  data: FiatDepositRequest & { businessId?: string | null }
): Promise<{ data: ApiResponse }> => {
  assertValidAmount(data.amount);
  const { businessId, rest } = splitBusinessId(
    data as unknown as Record<string, unknown>
  );
  const response = await create<Record<string, unknown>>(
    resolveBusinessUrlWithQuery(
      businessId,
      "/api/v2/transactions/fiat/deposit"
    ),
    rest
  );
  return wrapData(response);
};

export const createPayment = async (
  data: PaymentRequest & { businessId?: string | null }
): Promise<{ data: ApiResponse<V2Transaction> }> => {
  assertValidAmount(data.amount);
  const { businessId, rest } = splitBusinessId(
    data as unknown as Record<string, unknown>
  );
  const response = await create<V2Transaction>(
    resolveBusinessUrlWithQuery(businessId, "/api/v2/transactions/payment"),
    rest
  );
  return wrapData(response);
};

export const createEightyEightPayTrade = async (
  data: PaymentRequest & { businessId?: string | null }
): Promise<{ data: ApiResponse<V2Transaction> }> => {
  return createPayment(data);
};

export const transfer = async (
  data: TransferRequest & { businessId?: string | null }
): Promise<{ data: ApiResponse<V2Transaction> }> => {
  assertValidAmount(data.amount);
  const { businessId, rest } = splitBusinessId(
    data as unknown as Record<string, unknown>
  );
  const response = await create<V2Transaction>(
    resolveBusinessUrlWithQuery(businessId, "/api/v2/transactions/transfer"),
    rest
  );
  return wrapData(response);
};

export const donate = async (
  data: DonationRequest & { businessId?: string | null }
): Promise<{ data: ApiResponse<V2Transaction> }> => {
  assertValidAmount(data.amount);
  const { businessId, rest } = splitBusinessId(
    data as unknown as Record<string, unknown>
  );
  const response = await create<V2Transaction>(
    resolveBusinessUrlWithQuery(businessId, "/api/v2/transactions/donation"),
    rest
  );
  return wrapData(response);
};

export const mintZar8 = async (
  data: Zar8MintRequest & { businessId?: string | null }
): Promise<{ data: ApiResponse<V2Transaction> }> => {
  assertValidAmount(data.zarAmount, "zarAmount");
  const { businessId, rest } = splitBusinessId(
    data as unknown as Record<string, unknown>
  );
  const response = await create<V2Transaction>(
    resolveBusinessUrlWithQuery(businessId, "/api/v2/transactions/zar8/mint"),
    rest
  );
  return wrapData(response);
};

export const burnZar8 = async (
  data: Zar8BurnRequest & { businessId?: string | null }
): Promise<{ data: ApiResponse<V2Transaction> }> => {
  assertValidAmount(data.zar8Amount, "zar8Amount");
  const { businessId, rest } = splitBusinessId(
    data as unknown as Record<string, unknown>
  );
  const response = await create<V2Transaction>(
    resolveBusinessUrlWithQuery(businessId, "/api/v2/transactions/zar8/burn"),
    rest
  );
  return wrapData(response);
};

export const createTrade = async (data: {
  pairId?: string;
  businessId?: string | null;
  outgoingAssetSymbol: string;
  incomingAssetSymbol: string;
  outgoingAmount: number;
  incomingAmount: number;
  price?: number;
  side: "buy" | "sell";
  serviceFee?: number;
  notes?: string;
}): Promise<{ data: ApiResponse<V2Transaction> }> => {
  assertValidAmount(data.outgoingAmount, "outgoingAmount");
  assertValidAmount(data.incomingAmount, "incomingAmount");
  const { businessId, rest } = splitBusinessId(
    data as unknown as Record<string, unknown>
  );
  const response = await create<V2Transaction>(
    resolveBusinessUrlWithQuery(businessId, "/api/v2/transactions/trade"),
    rest
  );
  return wrapData(response);
};

export const withdrawQuote = async (
  data: FiatWithdrawQuoteRequest
): Promise<{ data: ApiResponse<Record<string, unknown>> }> => {
  assertValidAmount(data.requestedAmount ?? data.amount, "requestedAmount");
  const { businessId, rest } = splitBusinessId(
    data as unknown as Record<string, unknown>
  );
  const response = await create<Record<string, unknown>>(
    resolveBusinessUrlWithQuery(
      businessId,
      "/api/v2/transactions/fiat/withdraw-quote"
    ),
    rest
  );
  return wrapData(response);
};

export const createSendQuote = async (
  data: CryptoSendQuoteRequest & { businessId?: string | null }
) => {
  assertValidAmount(data.amount);
  const { businessId, rest } = splitBusinessId(
    data as unknown as Record<string, unknown>
  );
  return wrapData(
    await create<Record<string, unknown>>(
      resolveBusinessUrlWithQuery(
        businessId,
        "/api/v2/transactions/crypto/send-quote"
      ),
      rest
    )
  );
};

export const getUserSettlements = async (businessId?: string | null) =>
  get(
    resolveBusinessRoute(businessId, "/api/v2/transactions"),
    withBusinessContext(businessId, { type: "settlement" })
  );

export const getFees = async (asset: string, businessId?: string | null) =>
  get(
    resolveBusinessRoute(businessId, `/api/v2/transactions/fiat/withdraw-fee`),
    withBusinessContext(businessId, { asset })
  );

export const getEightyEightPayQuote = async (
  data: Record<string, unknown> & { businessId?: string | null }
) => {
  const { businessId, rest } = splitBusinessId(data);
  return wrapData(
    await create<Record<string, unknown>>(
      resolveBusinessUrlWithQuery(
        businessId,
        "/api/v2/transactions/payment/quote"
      ),
      rest
    )
  );
};
